import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import { TIBBO_DEVICE_ID } from '@appblocks/platforms/PlatformUtils';

export default class BeepNode extends BaseNode {
    constructor() {
        super();
        this.name = 'Buzzer Pattern';
        this.id = 'node_beep';
        this.icon = 'HiSpeakerWave';
        this.linkedFeature = 'general';
        this.requirements = ['tios'];

        this.inputs = [
            {
                name: 'Play Pattern',
                portId: 'beep.play',
                params: [
                    {
                        name: 'patternstring',
                        dataType: TibboDataType.STRING,
                        default: 'B-B-B',
                        regex: /^[bB\-~*]+$/,
                    },
                ],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'CANINT',
                name: 'Can Interrupt',
                settingType: SettingType.OPTION,
                required: true,
                options: [
                    {
                        name: 'Cannot Interrupt',
                        value: 'PL_PAT_NOINT',
                    },
                    {
                        name: 'Can Interrupt',
                        value: 'PL_PAT_CANINT',
                    },
                ],
                default: 'PL_PAT_CANINT',
            },
            {
                id: 'FREQ',
                name: 'Frequency (30-20000 Hz)',
                settingType: SettingType.STRINGVARIABLE,
                required: true,
                helpText: 'Frequency in Hz (30-20000)',
                default: '2700',
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const project: ProjectConfiguration = this.project;
            const links = project.getNodeLinks(this, 'beep.play');
            if (links.length > 0) {
                const link = links[0];
                const printstr = project.getNodeParameterValue(this, 'patternstring');
                return `Buzzer play ${printstr}`;
            }
        }
        return super.getSettingValue(settingId);
    }

    getName(): string {
        const project: ProjectConfiguration = this.project;
        const links = project.getNodeLinks(this, 'beep.play');
        if (links.length > 0) {
            const link = links[0];
            const printstr = project.getNodeParameterValue(this, 'patternstring');
            return `Buzzer play ${printstr}`;
        }
        return this.name;
    }

    getCodeBlocks(): CodeBlock[] {
        const blocks: CodeBlock[] = [];
        const project: ProjectConfiguration = this.project;

        const links = project.getNodeLinks(this, 'beep.play');
        const doneLinks = project.getNodeLinks(this, 'done');
        const doneBlock = doneLinks.length > 0 ? `${project.getCodeLocationStart()}${doneLinks[0].getSourceId()}${project.getCodeLocationEnd()}` : '';
        // default for gen2
        const CLOCK = 120000000;

        const freq = this.getSettingValue('FREQ');
        for (let i = 0; i < links.length; i += 1) {
            const link = links[i];

            const source: BaseNode = project.getFeature(link.getSettingValue('SOURCE_FEATURE')) as BaseNode;
            const sourceParams = source.getPort(link.getSettingValue('SOURCE_PORT'))?.params;
            blocks.push({
                location: link.getSourceId(),
                blockType: BlockType.CODE,
                id: link.getID(),
                template: `
                beep.divider = ${CLOCK} / (4 * ${project.parseCodeExpression(freq, sourceParams, TibboDataType.INTEGER)})
                beep.play(${project.getNodeParameter(link, 'patternstring')},${this.getSettingValue('CANINT')})
                ${doneBlock}`,
            });
        }

        return blocks;
    }

    getDetails(): string {
        return `##### Buzzer Pattern
This block loads a new buzzer pattern to play.

'-': the buzzer is off

'B' or 'b': the buzzer is on

'~': looped pattern (can reside anywhere in the pattern string)

'&ast;': double-speed pattern (can reside anywhere in the pattern string)`;
    }
}
